import { Edit, Eye, Plus, Search, Tags, Trash2 } from 'lucide-react'
import { useCallback, useEffect, useState } from 'react'
import { toast } from 'sonner'

import { useDebounce } from '../../hooks/useDebounce'
import { CatalogSelect } from '../components/ui/CatalogSelect'
import { Badge } from '../components/ui/badge'
import { Button } from '../components/ui/button'
import { FormError } from '../components/ui/FormError'
import { GlobalFilterBar } from '../components/ui/GlobalFilterBar'
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue, } from '../components/ui/select'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '../components/ui/dialog'
import { GlobalPaginator } from '../components/ui/global-paginator'
import { Input } from '../components/ui/input'
import { Textarea } from '../components/ui/textarea'
import {
  ProductUseCase,
  RuleEffect,
  UseCasePayload,
  UseCaseRule,
  useCaseService,
} from '../services/use-case.service'
import { useSessionStore } from '../store/auth/auth.store'

interface PreviewProduct {
  id: number
  name: string
  sku: string
  use_case_priority: number
}

interface PreviewPagination {
  total: number
  perPage: number
  from: number
  to: number
}



const emptyForm: UseCasePayload = {
  name: '',
  description: '',
  is_active: true,
  sort_order: 0,
  categories: [],
  departments: [],
  product_types: [],
  products: [],
}

interface RuleEditorProps {
  title: string
  catalog: string
  rules: UseCaseRule[]
  defaultPriority: number
  onChange: (rules: UseCaseRule[]) => void
  allowEffect?: boolean
}

function RuleEditor({ title, catalog, rules, defaultPriority, onChange, allowEffect = false }: RuleEditorProps) {
  const addRule = (item: { id: number; name?: string; title?: string; sku?: string }) => {
    if (rules.some((rule) => Number(rule.id) === Number(item.id))) return
    onChange([
      ...rules,
      {
        id: Number(item.id),
        name: item.name || item.title || item.sku || `ID ${item.id}`,
        sku: item.sku,
        priority: defaultPriority,
        effect: allowEffect ? 'include' : undefined,
      },
    ])
  }

  return (
    <section className="space-y-2 rounded-lg border border-border p-3">
      <div className="flex items-center justify-between gap-3">
        <h3 className="text-sm font-semibold text-popover-foreground">{title}</h3>
        <span className="text-xs text-popover-foreground/70">{rules.length} reglas</span>
      </div>
      <CatalogSelect
        catalog={catalog}
        returnObject
        onChange={addRule}
        placeholder={`Agregar ${title.toLowerCase()}...`}
      />
      <div className="max-h-40 space-y-2 overflow-y-auto pr-2">
        {rules.map((rule) => (
          <div key={rule.id} className="flex items-center gap-2 rounded bg-muted/30 p-2 text-sm border border-border/50">
            <div className="min-w-0 flex-1">
              <p className="truncate font-medium text-popover-foreground">{rule.name}</p>
              {rule.sku && <p className="truncate text-xs text-popover-foreground/70">SKU {rule.sku}</p>}
            </div>
            {allowEffect && (
  <Select
    value={rule.effect || 'include'}
    onValueChange={(value) =>
      onChange(
        rules.map((item) =>
          item.id === rule.id
            ? {
                ...item,
                effect: value as RuleEffect,
              }
            : item
        )
      )
    }
  >
    <SelectTrigger
      size="sm"
      className="w-24 text-popover-foreground"
      aria-label={`Efecto de ${rule.name}`}
    >
      <SelectValue />
    </SelectTrigger>

    <SelectContent>
      <SelectItem value="include">Incluir</SelectItem>
      <SelectItem value="exclude">Excluir</SelectItem>
    </SelectContent>
  </Select>
)}
            <label className="space-y-0.5 text-[11px] font-medium text-popover-foreground/70">
              Prioridad
              <Input
                aria-label={`Prioridad de ${rule.name}`}
                className="h-8 w-20 text-sm text-popover-foreground"
                type="number"
                min={0}
                max={100}
                value={rule.priority}
                onChange={(event) => onChange(rules.map((item) => item.id === rule.id
                  ? { ...item, priority: Math.max(0, Math.min(100, Number(event.target.value))) }
                  : item))}
              />
            </label>
            <button
              type="button"
              className="rounded p-1 text-destructive hover:bg-destructive/10"
              onClick={() => onChange(rules.filter((item) => item.id !== rule.id))}
              aria-label={`Quitar ${rule.name}`}
            >
              <Trash2 className="h-4 w-4" />
            </button>
          </div>
        ))}
      </div>
      {allowEffect && (
        <p className="text-xs text-popover-foreground/70">
          Las exclusiones siempre eliminan el producto, independientemente de su prioridad.
        </p>
      )}
    </section>
  )
}

export function UseCases() {
  const [items, setItems] = useState<ProductUseCase[]>([])
  const [search, setSearch] = useState('')
  const [page, setPage] = useState(1)
  const [pagination, setPagination] = useState({ total: 0, perPage: 15, from: 0, to: 0 })
  const [loading, setLoading] = useState(true)
  const [open, setOpen] = useState(false)
  const [saving, setSaving] = useState(false)
  const [editingId, setEditingId] = useState<number | null>(null)
  const [errors, setErrors] = useState<{
  name?: string
  }>({})
  const [form, setForm] = useState<UseCasePayload>(emptyForm)
  const [categoryRules, setCategoryRules] = useState<UseCaseRule[]>([])
  const [departmentRules, setDepartmentRules] = useState<UseCaseRule[]>([])
  const [productTypeRules, setProductTypeRules] = useState<UseCaseRule[]>([])
  const [productRules, setProductRules] = useState<UseCaseRule[]>([])
  const [preview, setPreview] = useState<{
    useCase: ProductUseCase
    products: PreviewProduct[]
    pagination: PreviewPagination
  } | null>(null)
  const [previewSearch, setPreviewSearch] = useState('')
  const [previewPage, setPreviewPage] = useState(1)
  const [previewLoading, setPreviewLoading] = useState(false)
  const debouncedPreviewSearch = useDebounce(previewSearch, 300)
  const canCreateUseCase = useSessionStore((s) => s.canAction('inventory.use-case.create'))
  const canEditUseCase = useSessionStore((s) => s.canAction('inventory.use-case.edit'))
  const canDeleteUseCase = useSessionStore((s) => s.canAction('inventory.use-case.delete'))
  const canChangeUseCaseStatus = useSessionStore((s) => s.canAction('inventory.use-case.status'))

  const load = useCallback(async () => {
    setLoading(true)
    try {
      const result = await useCaseService.list({ page, per_page: 15, search })
      setItems(result.data)
      setPagination({ total: result.total, perPage: result.per_page, from: result.from || 0, to: result.to || 0 })
    } finally {
      setLoading(false)
    }
  }, [page, search])

  useEffect(() => { void load() }, [load])

  const previewUseCaseId = preview?.useCase.id

  useEffect(() => {
    if (!previewUseCaseId) return

    let active = true
    setPreviewLoading(true)

    useCaseService.preview(previewUseCaseId, previewPage, debouncedPreviewSearch)
      .then((result) => {
        if (!active) return
        setPreview((current) => current ? {
          ...current,
          products: result.data,
          pagination: {
            total: result.total,
            perPage: result.per_page,
            from: result.from || 0,
            to: result.to || 0,
          },
        } : null)
      })
      .finally(() => {
        if (active) setPreviewLoading(false)
      })

    return () => { active = false }
  }, [previewUseCaseId, previewPage, debouncedPreviewSearch])

 const openCreate = () => {
  if (!canCreateUseCase) return

  setEditingId(null)
  setForm(emptyForm)
  setErrors({})
  setCategoryRules([])
  setDepartmentRules([])
  setProductTypeRules([])
  setProductRules([])
  setOpen(true)
}

  const openEdit = async (id: number) => {
    if (!canEditUseCase) return
    const useCase = await useCaseService.get(id)
    setEditingId(id)
    setForm({
      name: useCase.name,
      description: useCase.description || '',
      is_active: useCase.is_active,
      sort_order: useCase.sort_order,
      categories: [], departments: [], product_types: [], products: [],
    })
    setCategoryRules((useCase.categories || []).map((rule) => ({ ...rule, priority: rule.pivot.priority })))
    setDepartmentRules((useCase.departments || []).map((rule) => ({ ...rule, priority: rule.pivot.priority })))
    setProductTypeRules((useCase.product_types || []).map((rule) => ({ ...rule, priority: rule.pivot.priority })))
    setProductRules((useCase.product_overrides || []).map((rule) => ({
      ...rule,
      priority: rule.pivot.priority,
      effect: rule.pivot.effect,
    })))
    setErrors({})
    setOpen(true)
  }

    const save = async () => {
      if (editingId && !canEditUseCase) return
      if (!editingId && !canCreateUseCase) return

      const newErrors: {
    name?: string
  } = {}

  if (!form.name.trim()) {
    newErrors.name = 'El nombre es requerido'
  }

  setErrors(newErrors)

  if (Object.keys(newErrors).length > 0) {
    return
  }
    setSaving(true)
    const payload: UseCasePayload = {
      ...form,
      categories: categoryRules.map(({ id, priority }) => ({ id, priority })),
      departments: departmentRules.map(({ id, priority }) => ({ id, priority })),
      product_types: productTypeRules.map(({ id, priority }) => ({ id, priority })),
      products: productRules.map(({ id, priority, effect }) => ({ id, priority, effect: effect || 'include' })),
    }
    try {
      if (editingId) {
        await useCaseService.update(editingId, payload)
      } else {
        await useCaseService.create(payload)
      }
      toast.success(`Caso de uso ${editingId ? 'actualizado' : 'creado'} exitosamente.`)
      setOpen(false)
      await load()
    } catch {
      toast.error('No se pudo guardar el caso de uso.')
    } finally {
      setSaving(false)
    }
  }

  const showPreview = async (useCase: ProductUseCase) => {
    setPreviewSearch('')
    setPreviewPage(1)
    const detail = await useCaseService.get(useCase.id)
    setPreview({
      useCase: detail,
      products: [],
      pagination: { total: 0, perPage: 20, from: 0, to: 0 },
    })
  }

  const toggleStatus = async (useCase: ProductUseCase) => {
    if (!canChangeUseCaseStatus) return
    await useCaseService.updateStatus(useCase.id, !useCase.is_active)
    toast.success('Estado actualizado.')
    await load()
  }

  const remove = async (useCase: ProductUseCase) => {
    if (!canDeleteUseCase) return
    if (!window.confirm(`¿Eliminar el caso de uso “${useCase.name}”?`)) return
    await useCaseService.remove(useCase.id)
    toast.success('Caso de uso eliminado.')
    await load()
  }

return (
  <div className="space-y-6 p-6">
    <div className="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
      <div>
        <h1 className="text-2xl font-semibold text-foreground">
          Casos de uso
        </h1>

        <p className="mt-1 text-sm text-muted-foreground">
          Agrupa productos por la necesidad que resuelven.
        </p>
      </div>

      {canCreateUseCase && (
        <Button onClick={openCreate}>
          <Plus className="mr-2 h-4 w-4" />
          Nuevo caso
        </Button>
      )}
    </div>

    <GlobalFilterBar
      show={['search']}
      values={{
        search,
      }}
      onChange={(changes) => {
        if (changes.search !== undefined) {
          setSearch(changes.search)
          setPage(1)
        }
      }}
      customOptions={{
        searchPlaceholder: 'Buscar caso de uso...',
      }}
    />




      <div className="overflow-hidden rounded-lg border border-border bg-table-bg shadow-sm">
        <div className="overflow-x-auto">
          <table className="w-full whitespace-nowrap">
            <thead className="border-b border-border bg-table-header-bg">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-medium uppercase tracking-wider text-table-header-foreground">Caso</th>
                <th className="px-6 py-3 text-left text-xs font-medium uppercase tracking-wider text-table-header-foreground">Reglas</th>
                <th className="px-6 py-3 text-left text-xs font-medium uppercase tracking-wider text-table-header-foreground">Estado</th>
                <th className="px-6 py-3 text-right text-xs font-medium uppercase tracking-wider text-table-header-foreground">Acciones</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-border">
              {loading ? <tr><td className="px-6 py-12 text-center text-table-foreground" colSpan={4}>Cargando...</td></tr> : items.map((item) => (
                <tr key={item.id} className="transition-colors hover:bg-table-row-hover">
                  <td className="px-6 py-4"><p className="font-medium text-table-foreground">{item.name}</p><p className="text-sm text-table-foreground/70">{item.description}</p></td>
                  <td className="px-6 py-4 text-sm text-table-foreground">
                    {(item.categories_count || 0) + (item.departments_count || 0) + (item.product_types_count || 0)} catálogos · {item.product_overrides_count || 0} excepciones
                  </td>
                  <td className="px-6 py-4">
                    <button hidden={!canChangeUseCaseStatus} onClick={() => void toggleStatus(item)}><Badge
                          className={
                            item.is_active
                              ? 'bg-[#DCFCE6] text-[#008438]'
                              : 'bg-[#FFE2E2] text-[#C10008]'
                          }
                        >
                          {item.is_active ? 'Activo' : 'Inactivo'}
                        </Badge></button>
                    {!canChangeUseCaseStatus && <Badge className={item.is_active ? 'bg-primary/10 text-primary' : 'bg-muted text-table-foreground'}>{item.is_active ? 'Activo' : 'Inactivo'}</Badge>}
                  </td>
                  <td className="px-6 py-4 text-right">
                    <div className="flex items-center justify-end gap-2">
                      <button onClick={() => void showPreview(item)} title="Vista previa" className="rounded p-1.5 text-table-foreground transition-colors hover:bg-muted"><Eye className="h-4 w-4" /></button>
                      {canEditUseCase && <button onClick={() => void openEdit(item.id)} title="Editar" className="rounded p-1.5 text-table-foreground transition-colors hover:bg-muted"><Edit className="h-4 w-4" /></button>}
                      {canDeleteUseCase && <button onClick={() => void remove(item)} title="Eliminar" className="rounded p-1.5 text-destructive transition-colors hover:bg-destructive/10"><Trash2 className="h-4 w-4" /></button>}
                    </div>
                  </td>
                </tr>
              ))}
              {!loading && items.length === 0 && <tr><td colSpan={4} className="px-6 py-12 text-center text-table-foreground"><Tags className="mx-auto mb-3 h-8 w-8 text-table-foreground/30" />No hay casos de uso.</td></tr>}
            </tbody>
          </table>
        </div>
        {items.length > 0 && <GlobalPaginator totalItems={pagination.total} itemsPerPage={pagination.perPage} currentPage={page} from={pagination.from} to={pagination.to} onPageChange={setPage} />}
      </div>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-h-[92vh] overflow-y-auto sm:max-w-4xl bg-popover">
          <DialogHeader><DialogTitle className="flex items-center gap-2 text-lg font-semibold text-popover-foreground">{editingId ? 'Editar caso de uso' : 'Nuevo caso de uso'}</DialogTitle><DialogDescription className="text-popover-foreground/70">Una coincidencia con cualquier regla incluye el producto; una exclusión directa siempre gana.</DialogDescription></DialogHeader>
          <div className="grid gap-4 sm:grid-cols-2">
            <div className="space-y-1 text-sm">
  <label htmlFor="use-case-name" className="font-medium text-popover-foreground">
    Nombre <span className="text-destructive">*</span>
  </label>

    <Input
      id="use-case-name"
      value={form.name}
      aria-invalid={Boolean(errors.name)}
      aria-describedby={errors.name ? 'use-case-name-error' : undefined}
      onChange={(e) => {
        setForm({
          ...form,
          name: e.target.value,
        })

        if (errors.name) {
          setErrors({
            ...errors,
            name: undefined,
          })
        }
      }}
    />

    <FormError
      id="use-case-name-error"
      message={errors.name}
    />
  </div><label className="space-y-1 text-sm font-medium text-popover-foreground">
              Orden de aparición
              <Input type="number" min={0} value={form.sort_order} onChange={(e) => setForm({ ...form, sort_order: Number(e.target.value) })} />
              <span className="block text-xs text-popover-foreground/70">Los números menores aparecen primero.</span>
            </label>
            <label className="space-y-1 text-sm font-medium text-popover-foreground sm:col-span-2">Descripción<Textarea value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} /></label>
            <label hidden={!canChangeUseCaseStatus} className="flex items-center gap-2 text-sm font-medium text-popover-foreground sm:col-span-2"><input type="checkbox" checked={form.is_active} onChange={(e) => setForm({ ...form, is_active: e.target.checked })} className="rounded border-border text-primary focus:ring-primary/50" />Disponible</label>
            <p className="text-xs text-popover-foreground/70 sm:col-span-2">
              Las reglas con mayor prioridad aparecen primero en los resultados. Si un producto coincide con varias reglas, se utiliza la prioridad más alta.
            </p>
            <RuleEditor title="Categorías" catalog="categories" rules={categoryRules} defaultPriority={70} onChange={setCategoryRules} />
            <RuleEditor title="Departamentos" catalog="departments" rules={departmentRules} defaultPriority={50} onChange={setDepartmentRules} />
            <RuleEditor title="Tipos de producto" catalog="product_types" rules={productTypeRules} defaultPriority={30} onChange={setProductTypeRules} />
            <RuleEditor title="Productos específicos" catalog="products" rules={productRules} defaultPriority={100} onChange={setProductRules} allowEffect />
          </div>
          <DialogFooter><Button variant="outline" onClick={() => setOpen(false)}>Cancelar</Button><Button disabled={saving} onClick={() => void save()}>{saving ? 'Guardando...' : 'Guardar'}</Button></DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={preview !== null} onOpenChange={(value) => {
        if (!value) {
          setPreview(null)
          setPreviewSearch('')
          setPreviewPage(1)
        }
      }}>
        <DialogContent className="max-h-[80vh] overflow-y-auto sm:max-w-3xl bg-popover">
          <DialogHeader><DialogTitle className="flex items-center gap-2 text-lg font-semibold text-popover-foreground">Vista previa: {preview?.useCase.name}</DialogTitle><DialogDescription className="text-popover-foreground/70">Resultados ordenados por prioridad.</DialogDescription></DialogHeader>
          {preview && (
            <div className="space-y-3 rounded-lg border border-border bg-muted/30 p-3 text-sm">
              {(preview.useCase.departments?.length || 0) > 0 && (
                <div>
                  <p className="mb-1 text-xs font-semibold uppercase tracking-wide text-popover-foreground/70">Departamentos</p>
                  <div className="flex flex-wrap gap-1.5">
                    {preview.useCase.departments?.map((department) => (
                      <Badge key={department.id} className="bg-primary/10 text-primary">{department.name}</Badge>
                    ))}
                  </div>
                </div>
              )}
              {(preview.useCase.categories?.length || 0) > 0 && (
                <div>
                  <p className="mb-1 text-xs font-semibold uppercase tracking-wide text-popover-foreground/70">Categorías</p>
                  <div className="flex flex-wrap gap-1.5">
                    {preview.useCase.categories?.map((category) => (
                      <Badge key={category.id} className="bg-secondary text-secondary-foreground">{category.name}</Badge>
                    ))}
                  </div>
                </div>
              )}
              {(preview.useCase.product_types?.length || 0) > 0 && (
                <div>
                  <p className="mb-1 text-xs font-semibold uppercase tracking-wide text-popover-foreground/70">Tipos de producto</p>
                  <div className="flex flex-wrap gap-1.5">
                    {preview.useCase.product_types?.map((productType) => (
                      <Badge key={productType.id} className="bg-accent text-accent-foreground">{productType.name}</Badge>
                    ))}
                  </div>
                </div>
              )}
            </div>
          )}
          <div className="flex flex-col gap-3 sm:flex-row sm:items-end sm:justify-between">
            <label className="relative block flex-1 text-sm">
              <span className="mb-1 block font-medium text-popover-foreground">Buscar en este caso de uso</span>
              <Search className="absolute bottom-2.5 left-3 h-4 w-4 text-popover-foreground/70" />
              <Input
                type="search"
                value={previewSearch}
                onChange={(event) => {
                  setPreviewSearch(event.target.value)
                  setPreviewPage(1)
                }}
                placeholder="Nombre, código de barras..."
                className="pl-9"
              />
            </label>
            <p className="pb-2 text-sm text-popover-foreground/70">
              {previewLoading && preview?.pagination.total === 0
                ? 'Consultando productos...'
                : `${preview?.pagination.total || 0} productos coincidentes`}
            </p>
          </div>
          <div className="divide-y divide-border/50 rounded border border-border">
            {previewLoading ? (
              <p className="p-8 text-center text-popover-foreground/70">Cargando productos...</p>
            ) : (
              <>
                {preview?.products.map((product) => <div key={product.id} className="flex items-center justify-between p-3"><div><p className="font-medium text-popover-foreground">{product.name}</p><p className="text-xs text-popover-foreground/70">{product.sku}</p></div><Badge>{product.use_case_priority}</Badge></div>)}
                {preview?.products.length === 0 && <p className="p-8 text-center text-popover-foreground/70">No hay productos coincidentes.</p>}
              </>
            )}
          </div>
          {preview && preview.pagination.total > 0 && (
            <GlobalPaginator
              totalItems={preview.pagination.total}
              itemsPerPage={preview.pagination.perPage}
              currentPage={previewPage}
              from={preview.pagination.from}
              to={preview.pagination.to}
              onPageChange={setPreviewPage}
            />
          )}
        </DialogContent>
      </Dialog>
    </div>
  )
}
